import React, { useState } from 'react';
import { buildSessionCharts } from '../utils/meterSeries';

const VIEW_W = 720;
const VIEW_H = 220;
const PAD = { left: 52, right: 12, top: 16, bottom: 32 };

function formatTick(value, unit) {
  if (!Number.isFinite(value)) return '';
  if (unit === '%') return `${Math.round(value)}`;
  if (Math.abs(value) >= 100) return value.toFixed(0);
  if (Math.abs(value) >= 10) return value.toFixed(1);
  return value.toFixed(2);
}

function formatClock(ms) {
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function domain(chartsSeries) {
  const points = chartsSeries.flatMap((line) => line.points);
  if (!points.length) return null;
  let tMin = points[0].t;
  let tMax = points[0].t;
  let yMin = points[0].y;
  let yMax = points[0].y;
  points.forEach((point) => {
    tMin = Math.min(tMin, point.t);
    tMax = Math.max(tMax, point.t);
    yMin = Math.min(yMin, point.y);
    yMax = Math.max(yMax, point.y);
  });
  if (tMax === tMin) tMax = tMin + 60_000;
  if (yMax === yMin) {
    const pad = Math.abs(yMax) > 0 ? Math.abs(yMax) * 0.1 : 1;
    yMin -= pad;
    yMax += pad;
  } else {
    const pad = (yMax - yMin) * 0.08;
    yMin -= pad;
    yMax += pad;
  }
  if (yMin < 0 && points.every((point) => point.y >= 0)) yMin = 0;
  return { tMin, tMax, yMin, yMax };
}

function xOf(t, box) {
  const plotW = VIEW_W - PAD.left - PAD.right;
  return PAD.left + ((t - box.tMin) / (box.tMax - box.tMin)) * plotW;
}

function yOf(y, box) {
  const plotH = VIEW_H - PAD.top - PAD.bottom;
  return PAD.top + (1 - (y - box.yMin) / (box.yMax - box.yMin)) * plotH;
}

function pathFor(points, box) {
  return points.map((point, index) => {
    const cmd = index === 0 ? 'M' : 'L';
    return `${cmd}${xOf(point.t, box).toFixed(1)} ${yOf(point.y, box).toFixed(1)}`;
  }).join(' ');
}

function nearest(seriesList, box, x) {
  const plotW = VIEW_W - PAD.left - PAD.right;
  const ratio = Math.min(1, Math.max(0, (x - PAD.left) / plotW));
  const t = box.tMin + ratio * (box.tMax - box.tMin);
  let best = null;
  seriesList.forEach((line) => {
    line.points.forEach((point) => {
      const distance = Math.abs(point.t - t);
      if (!best || distance < best.distance) {
        best = { distance, point, line };
      }
    });
  });
  return best;
}

const Chart = ({ chart }) => {
  const [hover, setHover] = useState(null);
  const box = domain(chart.series);
  if (!box) return null;
  const yTicks = [box.yMax, (box.yMin + box.yMax) / 2, box.yMin];
  const plotBottom = VIEW_H - PAD.bottom;

  const onMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * VIEW_W;
    const hit = nearest(chart.series, box, x);
    setHover(hit ? { ...hit, x } : null);
  };

  return (
    <section style={{ marginTop: '0.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <h5 style={{ margin: 0, fontSize: '0.92rem', color: '#1a1a2e' }}>{chart.title}</h5>
        <span style={{ fontSize: '0.8rem', color: '#495057' }}>{chart.summary}</span>
      </div>
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        role="img"
        aria-label={`${chart.title}. ${chart.summary}`}
        style={{ width: '100%', height: 'auto', display: 'block', marginTop: '0.35rem', touchAction: 'pan-y' }}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        {yTicks.map((tick) => {
          const y = yOf(tick, box);
          return (
            <g key={tick}>
              <line x1={PAD.left} x2={VIEW_W - PAD.right} y1={y} y2={y} stroke="#e9ecef" strokeWidth="1" />
              <text x={PAD.left - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#6c757d">
                {formatTick(tick, chart.unit)}
              </text>
            </g>
          );
        })}
        <line x1={PAD.left} x2={PAD.left} y1={PAD.top} y2={plotBottom} stroke="#dee2e6" />
        <line x1={PAD.left} x2={VIEW_W - PAD.right} y1={plotBottom} y2={plotBottom} stroke="#dee2e6" />
        <text x={PAD.left} y={VIEW_H - 8} fontSize="11" fill="#6c757d">{formatClock(box.tMin)}</text>
        <text x={VIEW_W - PAD.right} y={VIEW_H - 8} textAnchor="end" fontSize="11" fill="#6c757d">{formatClock(box.tMax)}</text>
        {chart.series.map((line) => (
          <path
            key={line.id}
            d={pathFor(line.points, box)}
            fill="none"
            stroke={line.color}
            strokeWidth={line.id === 'offered' ? 1.5 : 2}
            strokeDasharray={line.dashed ? '5 4' : undefined}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
        {hover && (
          <g>
            <line
              x1={xOf(hover.point.t, box)}
              x2={xOf(hover.point.t, box)}
              y1={PAD.top}
              y2={plotBottom}
              stroke="#adb5bd"
              strokeDasharray="3 3"
            />
            <circle cx={xOf(hover.point.t, box)} cy={yOf(hover.point.y, box)} r="3.5" fill={hover.line.color} />
          </g>
        )}
      </svg>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', minHeight: '1.25rem', fontSize: '0.78rem', color: '#495057' }}>
        {chart.series.length > 1 && chart.series.map((line) => (
          <span key={line.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
            <span style={{
              width: 14,
              height: 0,
              borderTop: line.dashed ? `2px dashed ${line.color}` : `2px solid ${line.color}`,
              display: 'inline-block',
            }}
            />
            {line.label}
          </span>
        ))}
        {hover && (
          <span style={{ marginLeft: 'auto', fontWeight: 600, color: '#212529' }}>
            {formatClock(hover.point.t)}
            {' · '}
            {hover.line.label}
            {' '}
            {formatTick(hover.point.y, chart.unit)}
            {chart.unit === '%' ? '%' : ` ${chart.unit}`}
          </span>
        )}
      </div>
    </section>
  );
};

const MeterCharts = ({ rows }) => {
  const charts = buildSessionCharts(rows);
  if (!charts.length) {
    return (
      <div style={{ padding: '1rem', color: '#6c757d', textAlign: 'center', fontStyle: 'italic' }}>
        No power, energy, or state-of-charge samples for this session.
      </div>
    );
  }
  return (
    <div>
      {charts.map((chart) => <Chart key={chart.id} chart={chart} />)}
    </div>
  );
};

export default MeterCharts;
